import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../config";
import { Button } from "../ui/button";
import { Sparkles, CheckCircle } from "lucide-react";
import { RoutineCard } from "../RoutineCard";

export const RoutineCardsSection = () => {
  const [routines, setRoutines] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [routineOfTheWeekId, setRoutineOfTheWeekId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gallery?limit=3&sort=popular`)
      .then(r => { setRoutines(r.data.routines || []); setLoaded(true); })
      .catch(() => setLoaded(true));
    axios.get(`${API}/routines/of-the-week`)
      .then(r => setRoutineOfTheWeekId(r.data?.routine?.routine_id || null))
      .catch(() => {});
  }, []);

  // Mockup placeholders — remove once real routines exist
  const mockRoutines = [
    { routine_id: "mock1", name: "Breakout Control Routine", user_name: "Kavya R.", skin_type: "oily", products: [
      { name: "Minimalist Salicylic Cleanser", category: "Cleanser" },
      { name: "Derma Co Salicylic Acid Serum", category: "Serum" },
      { name: "Sebamed Clear Face Gel", category: "Moisturizer" },
    ], outcomes: { breakouts: "reduced", oiliness: "controlled" }, likes: 24 },
    { routine_id: "mock2", name: "Hydration Rescue Routine", user_name: "Arjun M.", skin_type: "dry", products: [
      { name: "Cetaphil Gentle Cleanser", category: "Cleanser" },
      { name: "Dot & Key Cica Serum", category: "Serum" },
      { name: "Neutrogena Hydro Boost Gel", category: "Moisturizer" },
    ], outcomes: { hydration: "improved", flaking: "reduced" }, likes: 18 },
    { routine_id: "mock3", name: "Sensitive Skin Calm Routine", user_name: "Meera I.", skin_type: "sensitive", products: [
      { name: "Simple Refreshing Face Wash", category: "Cleanser" },
      { name: "The Ordinary Niacinamide", category: "Serum" },
      { name: "Cetaphil Moisturizing Lotion", category: "Moisturizer" },
    ], outcomes: { redness: "reduced", irritation: "calmed" }, likes: 31 },
  ];

  const displayRoutines = routines.length > 0 ? routines : (loaded ? mockRoutines : []);
  const showSection = displayRoutines.length > 0 || !loaded;

  if (!showSection) return null;

  return (
    <section className="py-16 px-4 bg-gradient-to-br from-[#5B6BA8]/[0.03] to-slate-50/50" data-testid="routine-cards-section">
      <div className="max-w-6xl mx-auto">
        {/* Compact header — 2 lines max */}
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-2 leading-tight text-gradient-brand" data-testid="routines-heading">
            Routines That Actually Worked
          </h2>
          <p className="text-sm sm:text-base text-foreground max-w-lg mx-auto">
            Most skincare issues aren't solved by one product. <span className="font-medium">The right routine matters.</span>
          </p>
        </div>

        {/* Routine Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {displayRoutines.slice(0, 3).map(r => (
            <RoutineCard key={r.routine_id} routine={r} routineOfTheWeekId={routineOfTheWeekId} />
          ))}
        </div>

        {/* Value props + CTAs */}
        <div className="flex flex-col items-center gap-5">
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              "Routines that reduced breakouts, oiliness & texture",
              "Compare across skin types",
              "Share yours & help others",
            ].map((item) => (
              <span key={item} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border border-slate-200/60 text-xs text-muted-foreground">
                <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" />
                {item}
              </span>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/routines">
              <Button className="rounded-full gap-2 bg-[#3B5998] hover:bg-[#2d4373] text-white" data-testid="browse-routines-btn">
                Browse Routines
              </Button>
            </Link>
            <Link to="/my-routines">
              <Button variant="outline" className="rounded-full gap-2 text-[#3B5998] border-[#3B5998] hover:bg-[#3B5998]/5" data-testid="share-routine-btn">
                <Sparkles className="w-3.5 h-3.5" /> Share Your Routine
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
